import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Send } from "lucide-react";
import { submitContact } from "../lib/api";
import { toast } from "sonner";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Your message has been received. We will respond shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send message";
      toast.error(typeof msg === "string" ? msg : "Could not send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0 }}
        >
          <span className="text-[10px] uppercase tracking-label gold-text">
            Contact
          </span>
          <h1 className="mt-5 font-serif text-5xl md:text-7xl leading-[1.0]">
            We're here to help.
          </h1>
          <div className="divider-gold mt-8" />
        </motion.div>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.0 }}
            className="lg:col-span-4 space-y-10"
          >
            <div>
              <p className="text-[10px] uppercase tracking-label gold-text mb-3">
                Direct
              </p>
              <p className="text-[var(--secondary-text)] leading-loose">
                Whether you have a question about our products or are simply inquiring about our brand, we value your communication. We will do our best to respond to you in a timely manner.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <MapPin size={16} className="gold-text mt-1" strokeWidth={1.2} />
                <div>
                  <p className="text-[10px] uppercase tracking-label text-[var(--secondary-text)]">
                    
                  </p>
                  <p className="font-serif text-lg">Fremont, OH</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.0, delay: 0.1 }}
            className="lg:col-span-8 lg:pl-12 lg:border-l hairline space-y-8"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-[10px] uppercase tracking-label gold-text mb-3">
                  Name
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  type="text"
                  className="field"
                  placeholder="Your name"
                  data-testid="contact-name-input"
                  required
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-label gold-text mb-3">
                  Email
                </label>
                <input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  type="email"
                  className="field"
                  placeholder="youremail@domain.com"
                  data-testid="contact-email-input"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-label gold-text mb-3">
                Subject
              </label>
              <input
                name="subject"
                value={form.subject}
                onChange={handleChange}
                type="text"
                className="field"
                placeholder="Optional"
                data-testid="contact-subject-input"
              />
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-label gold-text mb-3">
                Message
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                className="field"
                placeholder="Write to us"
                rows={5}
                data-testid="contact-message-input"
                required
              />
            </div>

            <div className="flex items-center justify-between pt-4">
              <p className="text-[10px] uppercase tracking-label text-[var(--secondary-text)]">
                
              </p>
              <button
                type="submit"
                disabled={loading}
                className="btn-secondary inline-flex items-center gap-3"
                data-testid="contact-submit-btn"
              >
                {loading ? "Sending…" : "Send Message"}
                <Send size={14} />
              </button>
            </div>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
